import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Clock, LoaderCircle, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CameraCapture } from "@/components/CameraCapture";
import { StatusBadge } from "@/components/StatusBadge";
import { api, fmtKLDateTime } from "@/lib/api";
import { useLang } from "@/lib/i18n";

/*
 * GPS is requested IN PARALLEL with camera initialisation — not after capture
 * (spec §4). maximumAge: 0 is mandatory so a stale cached fix is never reused.
 * The punch is NEVER blocked on GPS: on timeout/denial it is submitted with a
 * location hint and flagged for review server-side.
 */
function useGeolocation() {
  const [state, setState] = useState({ status: "acquiring", coords: null });
  useEffect(() => {
    if (!navigator.geolocation) {
      setState({ status: "unavailable", coords: null });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setState({
          status: accuracy > 150 ? "low_accuracy" : "ok",
          coords: { latitude, longitude, accuracy },
        });
      },
      (err) => {
        setState({ status: err.code === 1 ? "denied" : "unavailable", coords: null });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }, []);
  return state;
}

export default function CapturePage() {
  const { type } = useParams(); // clock_in | clock_out
  const navigate = useNavigate();
  const { t } = useLang();
  const queryClient = useQueryClient();
  const gps = useGeolocation();
  const [blob, setBlob] = useState(null);
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const clientTsRef = useRef(null);

  // --- location (site) selection: required when active locations exist ---
  const { data: locData } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data,
  });
  const activeLocations = (locData?.items || []).filter((l) => l.isActive);
  const [siteId, setSiteId] = useState(() => localStorage.getItem("rk-last-site") || "");
  const locationRequired = activeLocations.length > 0;
  const siteValid = !locationRequired || activeLocations.some((l) => l.id === siteId);

  const isClockOut = type === "clock_out";
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const handleCapture = useCallback((b) => {
    clientTsRef.current = new Date().toISOString(); // diagnostics only
    setBlob(b);
  }, []);

  const submit = async () => {
    if (!blob) return;
    if (locationRequired && !siteValid) {
      setError(t("locationRequired"));
      return;
    }
    setBusy(true);
    setError("");
    const fd = new FormData();
    fd.append("photo", blob, "selfie.jpg");
    fd.append("punchType", type);
    if (locationRequired && siteId) fd.append("locationId", siteId);
    if (gps.coords) {
      fd.append("latitude", String(gps.coords.latitude));
      fd.append("longitude", String(gps.coords.longitude));
      fd.append("accuracyM", String(gps.coords.accuracy));
    } else {
      fd.append("locationHint", gps.status === "denied" ? "denied" : "unavailable");
    }
    if (clientTsRef.current) fd.append("clientTimestamp", clientTsRef.current);
    if (isClockOut && summary.trim()) fd.append("summary", summary.trim().slice(0, 2000));
    try {
      const { data } = await api.post("/punches", fd);
      if (siteId) localStorage.setItem("rk-last-site", siteId);
      queryClient.invalidateQueries({ queryKey: ["my-status"] });
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "location_required" || detail === "invalid_location") {
        setError(t("locationRequired"));
      } else {
        setError(detail || "Failed to submit — please try again");
      }
    } finally {
      setBusy(false);
    }
  };

  // ---------- result screen ----------
  if (result) {
    const p = result.punch;
    // Staff never see face-engine internals: a punch recorded while the
    // engine was unavailable ("unverified") is presented as a normal success.
    // Supervisors see the real state via the engine banner / review queue.
    const ok = p.matchResult === "pass" || p.matchResult === "unverified";
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-background px-6">
        {ok ? (
          <CheckCircle2 className="h-16 w-16 text-emerald-600" />
        ) : (
          <Clock className="h-16 w-16 text-amber-600" />
        )}
        <h1 data-testid="punch-result-title" className="text-center text-2xl font-semibold tracking-tight">
          {ok ? t("punchRecorded") : t("punchNeedsReview")}
        </h1>
        {!ok && (
          <p data-testid="punch-result-message" className="max-w-sm text-center text-sm text-muted-foreground">
            {p.matchResult === "no_face_detected" ? t("punchNoFaceMsg") : t("punchFailMsg")}
          </p>
        )}
        <div className="rounded-xl border border-border bg-card px-5 py-4 text-center shadow-sm">
          <p className="text-xs text-muted-foreground">{t("serverTime")}</p>
          <p data-testid="punch-result-server-time" className="font-mono text-lg font-semibold tabular-nums">
            {fmtKLDateTime(p.serverTimestamp)}
          </p>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <StatusBadge kind="review" value={p.reviewStatus} />
            <StatusBadge kind="location" value={p.locationStatus} />
          </div>
        </div>
        <div className="flex gap-3">
          <Button data-testid="punch-result-done-button" onClick={() => navigate("/staff")} className="h-12 px-8">
            {t("done")}
          </Button>
          <Button variant="ghost" onClick={() => navigate("/staff/history")} data-testid="punch-result-history-button" className="h-12">
            {t("viewHistory")}
          </Button>
        </div>
      </div>
    );
  }

  // ---------- capture screen ----------
  return (
    <div className="rk-camera-screen flex flex-col">
      <div className="rk-safe-top z-10 flex items-center justify-between px-4 py-2">
        <Button
          variant="ghost" size="icon"
          onClick={() => navigate("/staff")}
          data-testid="capture-back-button"
          className="text-white hover:bg-white/10"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <span className="text-sm font-semibold">
          {isClockOut ? t("clockOut") : t("clockIn")}
        </span>
        <StatusBadge kind="location" value={gps.status} testId="gps-status-badge" />
      </div>

      {gps.status === "denied" && isIos && (
        <p data-testid="gps-denied-ios-hint" className="z-10 mx-4 mb-1 rounded-lg bg-black/50 p-2 text-center text-xs text-white/80">
          {t("gpsDeniedIosHint")}
        </p>
      )}

      <CameraCapture
        onCapture={handleCapture}
        capturedBlob={blob}
        onRetake={() => { setBlob(null); setError(""); }}
        disabled={busy}
      />

      {blob && (
        <div className="z-10 space-y-3 bg-black/70 p-4 rk-safe-bottom">
          {locationRequired && (
            <div className="space-y-1">
              <label className="flex items-center gap-1 text-xs font-medium text-white/80">
                <MapPin className="h-3.5 w-3.5" /> {t("locationLabel")} <span className="text-rose-300">*</span>
              </label>
              <Select value={siteId || undefined} onValueChange={(v) => { setSiteId(v); setError(""); }}>
                <SelectTrigger
                  data-testid="punch-location-select"
                  className="border-white/20 bg-white/10 text-white data-[placeholder]:text-white/50"
                >
                  <SelectValue placeholder={t("selectLocation")} />
                </SelectTrigger>
                <SelectContent>
                  {activeLocations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {isClockOut && (
            <Textarea
              data-testid="summary-input"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={t("summaryPlaceholder")}
              maxLength={2000}
              rows={2}
              className="border-white/20 bg-white/10 text-white placeholder:text-white/50"
            />
          )}
          {error && <p data-testid="punch-submit-error" className="text-sm font-medium text-rose-300">{error}</p>}
          <Button
            data-testid="punch-submit-button"
            onClick={submit}
            disabled={busy || (locationRequired && !siteValid)}
            className="h-14 w-full text-base font-semibold"
          >
            {busy ? (<><LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> {t("submitting")}</>) : t("submit")}
          </Button>
        </div>
      )}
    </div>
  );
}
